import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';

import type { MatchAnswers } from './answers';

/** The resource route the brief is posted to. */
export const GET_MATCHED_SUBMIT_ROUTE = '/remix-api/get-matched/submit';

interface SubmitResponse {
    success: boolean;
    errorMessage?: string | null;
    submissionId?: string | number | null;
    buildHubURLSegment?: string | null;
}

/** What a filed brief is afterwards: a reference, and the project it belongs to. */
export interface FiledBrief {
    submissionId: string;
    buildHubURLSegment: string | null;
}

/**
 * Multi-valued answers reach the server as one comma-joined string, because the
 * submission stores each as a single column. Joining here rather than at the
 * step keeps the answer shape honest about being a list.
 */
const joinList = (values: string[]) => values.join(', ');

/**
 * The chosen place in the shape `PlaceDetailTypeInput` expects — the same input
 * a project record is created from, so the address does not have to be parsed
 * back apart on the way in.
 *
 * `FullAddress` is required by that type, so a place without one is not sent at
 * all. Nothing is lost when that happens: the area label still travels, and
 * that is what matching reads.
 */
const toPlaceDetailInput = (place: DetailedPlace | null) => {
    if (!place?.fullAddress?.trim()) return null;

    return {
        Address1: place.address1,
        Address2: place.address2,
        AddressID: place.addressID,
        City: place.city,
        Country: place.country,
        Depth: place.depth,
        District: place.district,
        Division: place.division,
        FullAddress: place.fullAddress,
        Latitude: place.latitude,
        Longitude: place.longitude,
        ParentID: place.parentID,
        PlaceName: place.placeName,
        PlaceType: place.placeType,
        PostCode: place.postCode,
        Region: place.region,
        ServiceableAreaID: place.serviceableAreaID,
        Slug: place.slug,
        State: place.state,
        Suburb: place.suburb,
    };
};

const buildFormData = (answers: MatchAnswers): FormData => {
    const formData = new FormData();

    formData.append('enquiryType', answers.enquiryType);
    formData.append('userType', answers.userType);
    formData.append('region', answers.region);
    formData.append('projectType', answers.projectType);
    formData.append('projectScope', answers.projectScope);
    formData.append('style', joinList(answers.styles));
    formData.append('budget', answers.budget);
    formData.append('professionalType', joinList(answers.professionalTypes));
    formData.append('priorities', joinList(answers.priorities));
    formData.append('startTiming', answers.startTiming);
    formData.append('contactUrgency', answers.contactUrgency);
    formData.append('description', answers.description);

    const locationData = toPlaceDetailInput(answers.location);
    if (locationData) {
        formData.append('locationData', JSON.stringify(locationData));
    }

    if (answers.buildHubID !== null) {
        formData.append('buildHubID', String(answers.buildHubID));
    }

    formData.append(
        'submittedFromURL',
        typeof window === 'undefined' ? '' : window.location.href,
    );

    answers.attachments.forEach((file, index) => {
        formData.append(`Attachments[${index}]`, file, file.name);
    });

    return formData;
};

/**
 * Posts the brief and settles, so the questionnaire engine can await it.
 *
 * A plain `fetch` rather than a Remix fetcher: the engine models submission as
 * a promise that resolves or throws, and a fetcher reports through re-renders
 * instead — which is what forced the previous implementation to keep a flag for
 * whether the state it was reading belonged to the current attempt.
 */
export const submitGetMatchedBrief = async (
    answers: MatchAnswers,
): Promise<FiledBrief | null> => {
    const response = await fetch(GET_MATCHED_SUBMIT_ROUTE, {
        body: buildFormData(answers),
        method: 'POST',
    });

    if (!response.ok) {
        throw new Error(
            "We couldn't send your brief just now. Please try again.",
        );
    }

    const result = (await response.json()) as SubmitResponse;

    if (!result.success) {
        throw new Error(
            result.errorMessage ??
                "We couldn't send your brief just now. Please try again.",
        );
    }

    if (result.submissionId == null) return null;

    return {
        submissionId: String(result.submissionId),
        buildHubURLSegment: result.buildHubURLSegment ?? null,
    };
};
